import React, { useState } from "react";
import {
  Box,
  Button,
  Popover,
  Typography,
  makeStyles
} from "@material-ui/core";
import { format as formatDate, isValid } from "date-fns";
import GetAppIcon from "@material-ui/icons/GetApp";
import DescriptionIcon from "@material-ui/icons/Description";
import CloseIcon from "@material-ui/icons/Close";
import { PrimaryButton } from "../../../elements/Button";
import I18nText from "../../../elements/I18nText";
import SegmentedControl from "./SegmentedControl";
import ExportDateRange from "./ExportDateRange";

const useStyles = makeStyles(theme => ({
  panel: {
    width: 320,
    padding: theme.spacing(2.5)
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  title: {
    fontSize: 15,
    fontWeight: 500
  },
  closeButton: {
    minWidth: 0,
    padding: 4,
    color: theme.palette.text.secondary
  },
  field: {
    marginBottom: theme.spacing(2)
  },
  fieldLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 6
  },
  summary: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    padding: "8px 10px",
    marginBottom: theme.spacing(2)
  },
  summaryIcon: {
    color: "rgba(0, 0, 0, 0.38)",
    marginRight: 6
  },
  summaryText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    "& > * + *": {
      marginLeft: 8
    }
  }
}));
// (simple_consumption = CSV only, extended_consumption = XLSX or PDF)
const SOURCE_FORMATS = {
  consumption: ["csv"],
  measurement: ["xlsx", "pdf"]
};

const ExportPanel = ({ currentView, onExport }) => {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [source, setSource] = useState(currentView || "consumption");
  const [format, setFormat] = useState(
    SOURCE_FORMATS[currentView || "consumption"][0]
  );
  const [rangeType, setRangeType] = useState("shown");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  const handleSourceChange = value => {
    setSource(value);
    setFormat(SOURCE_FORMATS[value][0]);
  };

  const range =
    rangeType === "shown"
      ? { type: "shown" }
      : { type: "custom", from: from, to: to };

  const handleExport = () => {
    onExport({ source: source, format: format, range: range });
    close();
  };

  // KeyboardDatePicker emits an Invalid Date while the user is still typing,
  // so we need to guard with isValid before formatting
  const formatSafe = date =>
    isValid(date) ? formatDate(date, "dd.MM.yyyy") : "–";

  const rangeSummary =
    rangeType === "shown" ? (
      <I18nText>devices.export.range.shown</I18nText>
    ) : (
      `${formatSafe(from)} – ${formatSafe(to)}`
    );

  return (
    <>
      <PrimaryButton
        icon={<GetAppIcon />}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <I18nText>devices.export.action</I18nText>
      </PrimaryButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={classes.panel}>
          <div className={classes.header}>
            <Typography className={classes.title}>
              <I18nText>devices.export.title</I18nText>
            </Typography>
            <Button className={classes.closeButton} onClick={close}>
              <CloseIcon fontSize="small" />
            </Button>
          </div>

          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <I18nText>devices.export.source.label</I18nText>
            </div>
            <SegmentedControl
              value={source}
              onChange={handleSourceChange}
              options={[
                {
                  value: "consumption",
                  label: <I18nText>devices.export.source.consumption</I18nText>
                },
                {
                  value: "measurement",
                  label: <I18nText>devices.export.source.measurement</I18nText>
                }
              ]}
            />
          </div>

          {SOURCE_FORMATS[source].length > 1 && (
            <div className={classes.field}>
              <div className={classes.fieldLabel}>
                <I18nText>devices.export.format.label</I18nText>
              </div>
              <SegmentedControl
                value={format}
                onChange={setFormat}
                options={SOURCE_FORMATS[source].map(value => ({
                  value,
                  label: <I18nText>{`devices.export.format.${value}`}</I18nText>
                }))}
              />
            </div>
          )}

          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <I18nText>devices.export.range.label</I18nText>
            </div>
            <SegmentedControl
              value={rangeType}
              onChange={setRangeType}
              options={[
                {
                  value: "shown",
                  label: <I18nText>devices.export.range.shown</I18nText>
                },
                {
                  value: "custom",
                  label: <I18nText>devices.export.range.custom</I18nText>
                }
              ]}
            />
            {rangeType === "custom" && (
              <ExportDateRange
                from={from}
                to={to}
                onChangeFrom={setFrom}
                onChangeTo={setTo}
              />
            )}
          </div>

          <div className={classes.summary}>
            <DescriptionIcon fontSize="small" className={classes.summaryIcon} />
            <span className={classes.summaryText}>
              {format.toUpperCase()} &middot; {rangeSummary}
            </span>
          </div>

          <Box className={classes.actions}>
            <Button onClick={close}>
              <I18nText>devices.export.cancel</I18nText>
            </Button>
            <PrimaryButton icon={<GetAppIcon />} onClick={handleExport}>
              <I18nText>devices.export.action</I18nText>
            </PrimaryButton>
          </Box>
        </div>
      </Popover>
    </>
  );
};

export default ExportPanel;
